import type { CSSProperties, ComponentPropsWithoutRef } from 'react';
import './keyframes.css';
import { useSize } from './useSize';
import { useCSSTransition } from './useCSSTransition';

type CollapseProps = ComponentPropsWithoutRef<'div'> & {
   show: boolean | undefined | null;
   animateOpacity?: boolean;
};

export function Collapse({
   show = false,
   animateOpacity = false,
   style,
   children,
   ...otherProps
}: CollapseProps) {
   const transition = useCSSTransition({ show });
   const [ref, size] = useSize();
   const height = size?.height;

   const activeState = show ? (height == null ? undefined : transition.state) : transition.state;

   return (
      <div
         hidden={activeState === 'hide'}
         style={
            {
               ...style,
               overflow: 'hidden',
               '--slide-height': height == null ? undefined : `${height}px`,
               ...getCollapseStateStyle(activeState, height, animateOpacity),
            } as CSSProperties
         }
         onAnimationEnd={transition.onAnimationEnd}
         {...otherProps}
      >
         <div ref={ref}>{children}</div>
      </div>
   );
}

function getCollapseStateStyle(
   state: string | undefined,
   height: number | undefined,
   animateOpacity: boolean
): CSSProperties {
   switch (state) {
      case 'hide': {
         return { height: 0, ...(animateOpacity ? { opacity: 0 } : {}) };
      }
      case 'show': {
         return {
            height: height == null ? undefined : `${height}px`,
            ...(animateOpacity ? { opacity: 1 } : {}),
         };
      }
      case 'enter': {
         return {
            animation: `${animateOpacity ? 'ui-slide-fade-in' : 'ui-slide-in'} 250ms ease-in-out forwards`,
         };
      }
      case 'exit': {
         return {
            animation: `${animateOpacity ? 'ui-slide-fade-out' : 'ui-slide-out'} 250ms ease-in-out forwards`,
         };
      }
      default: {
         return {};
      }
   }
}
